// 角色管理頁（查詢、建立、編輯、刪除）- Material UI 版本
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';

export default function RoleAdminPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, roleId: null });

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/roles', { credentials: 'include', cache: 'no-store' });
      const roles = await response.json();
      console.log('Fetched roles from /api/roles:', roles);
      setData(roles.map(r => ({ ...r, key: r.id })));
    } catch (error) {
      setSnackbar({ open: true, message: '載入角色列表失敗', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // 初始化取得角色列表
  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = (record) => {
    setEditing(record);
    setFormData({ name: record.name });
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditing(null);
    setFormData({ name: '' });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`/api/roles/${id}`, { 
        method: 'DELETE', 
        credentials: 'include' 
      });
      const data = await response.json();
      
      if (response.ok) {
        setSnackbar({ open: true, message: '刪除成功', severity: 'success' });
        fetchData();
      } else {
        setSnackbar({ open: true, message: data.error || '刪除失敗', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: '刪除失敗', severity: 'error' });
    }
    setDeleteDialog({ open: false, roleId: null });
  };

  const handleOk = async () => {
    if (!formData.name.trim()) {
      setSnackbar({ open: true, message: '請輸入角色名稱', severity: 'warning' });
      return;
    }

    try {
      console.log('Frontend: updating role id=', editing?.id, 'with values=', formData);
      
      const url = editing ? `/api/roles/${editing.id}` : '/api/roles';
      const method = editing ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      let responseData = {};
      try { 
        responseData = await response.json(); 
      } catch { 
        responseData = {}; 
      }

      if (response.ok) {
        setSnackbar({ 
          open: true, 
          message: editing ? '更新成功' : '新增成功', 
          severity: 'success' 
        });
        setModalOpen(false);
        setEditing(null);
        setFormData({ name: '' });
        fetchData();
      } else {
        const errorMessage = typeof responseData.error === 'string' ? responseData.error :
                           typeof responseData.message === 'string' ? responseData.message :
                           JSON.stringify(responseData) || (editing ? '更新失敗' : '新增失敗');
        setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ 
        open: true, 
        message: err?.message || (editing ? '更新失敗' : '新增失敗'), 
        severity: 'error' 
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
        角色管理
      </Typography>
      
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleAdd}
        sx={{ mb: 2 }}
        size={isMobile ? 'small' : 'medium'}
      >
        新增角色
      </Button>
      
      <TableContainer component={Paper} elevation={1}>
        <Table size={isMobile ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              <TableCell>角色名稱</TableCell>
              <TableCell align="center">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((role) => (
              <TableRow key={role.id} hover>
                <TableCell>
                  <Typography variant="body2">
                    {role.name}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="編輯">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEdit(role)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="刪除">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteDialog({ open: true, roleId: role.id })}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {data.length === 0 && (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    暫無角色資料
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 新增/編輯對話框 */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editing ? '編輯角色' : '新增角色'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="角色名稱"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ name: e.target.value })}
            required
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>
            取消
          </Button>
          <Button 
            onClick={handleOk}
            variant="contained"
          >
            {editing ? '更新' : '新增'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 刪除確認對話框 */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, roleId: null })}
      >
        <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>
          <Typography>確定要刪除此角色嗎？此操作無法復原。</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, roleId: null })}>
            取消
          </Button>
          <Button 
            onClick={() => handleDelete(deleteDialog.roleId)}
            color="error"
            variant="contained"
          >
            刪除
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}